import type { IObservableArray } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Recommendation } from '@feathr/blackbox';

interface IUseCardStateProps {
  /** Whether there were active recommendations on load */
  hasActiveRecommendationsOnLoad: boolean;
  /** Whether any recommendations were dismissed during this session */
  hasRecommendationsDismissed: boolean;
  /** The function to start the confetti animation */
  onStartConfetti: () => void;
  /** The recommendation models */
  recommendations: IObservableArray<Recommendation>;
}

interface IUseCardStateReturnProps {
  /** Whether to show the empty state */
  showEmptyState: boolean;
  /** Whether to show the recommendations card */
  showRecommendationCard: boolean;
}

/**
 * Hook to manage the card state of the recommendations
 * @param hasActiveRecommendationsOnLoad - Whether there were active recommendations on load
 * @param hasRecommendationsDismissed - Whether any recommendations were dismissed during this session
 * @param onStartConfetti - The function to start the confetti animation
 * @param recommendations - The recommendations collection
 */
function useCardState({
  hasActiveRecommendationsOnLoad,
  hasRecommendationsDismissed,
  onStartConfetti,
  recommendations,
}: IUseCardStateProps): IUseCardStateReturnProps {
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [showRecommendationCard, setShowRecommendationCard] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleHideRecommendationCardOnDelay = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Remove recommendation card after 5 seconds
    timeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        setShowRecommendationCard(false);
      });
    }, 5000);
  }, [setShowRecommendationCard]);

  useEffect(() => {
    // Check if all recommendations are gone (either applied or dismissed)
    const allRecommendationsGone = hasActiveRecommendationsOnLoad && recommendations.length === 0;

    if (allRecommendationsGone) {
      if (hasRecommendationsDismissed) {
        // If any were dismissed, hide the card entirely
        setShowRecommendationCard(false);
      } else {
        // If all were applied, show the "You did it!" state with confetti
        handleToggleEmptyState();

        // Hide the card after 5 seconds
        handleHideRecommendationCardOnDelay();
      }
    }
    return;
  }, [recommendations.length, hasActiveRecommendationsOnLoad, hasRecommendationsDismissed]);

  function handleToggleEmptyState(): void {
    if (!showEmptyState) {
      setShowEmptyState(true);
      requestAnimationFrame(() => {
        onStartConfetti();
      });
    } else {
      setShowEmptyState(false);
    }
  }

  return {
    showEmptyState,
    showRecommendationCard,
  };
}

export default useCardState;
